import { first } from 'lodash';
import { useEffect } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { getNodes } from '@/utils/graphqlUtils';

import {
  type ReviewAiSuggestionsFromNotification_AiSuggestionFragment,
  useReviewAiSuggestionsFromNotificationAutoAcceptedQuery,
  useReviewAiSuggestionsFromNotificationHouseholdMetadataQuery,
  useReviewAiSuggestionsFromNotificationSuggestionsV2Query,
} from './graphql/ReviewAISuggestionsFromNotification.generated';

interface UseOutstandingAISuggestionsProps {
  householdId: string | null;
}

export function useOutstandingAISuggestions({
  householdId,
}: UseOutstandingAISuggestionsProps) {
  const { showFeedback } = useFeedback();

  const suggestionsQueryResult =
    useReviewAiSuggestionsFromNotificationSuggestionsV2Query({
      variables: {
        householdId: householdId!,
      },
      skip: !householdId,
      fetchPolicy: 'cache-and-network',
    });

  const autoAcceptedResult =
    useReviewAiSuggestionsFromNotificationAutoAcceptedQuery({
      variables: {
        householdId: householdId!,
      },
      skip: !householdId,
      fetchPolicy: 'cache-and-network',
    });

  const householdQueryResult =
    useReviewAiSuggestionsFromNotificationHouseholdMetadataQuery({
      variables: {
        householdId: householdId!,
      },
      skip: !householdId,
      fetchPolicy: 'cache-and-network',
    });

  const error =
    suggestionsQueryResult.error ||
    householdQueryResult.error ||
    autoAcceptedResult.error;

  useEffect(() => {
    if (!error) {
      return;
    }

    showFeedback(
      'Error loading outstanding suggestions. Please refresh the page and try again.'
    );
  }, [error, showFeedback]);

  const suggestions = getNodes(
    suggestionsQueryResult.data?.aiSuggestions
  ) as ReviewAiSuggestionsFromNotification_AiSuggestionFragment[];
  const household = first(getNodes(householdQueryResult.data?.households));

  const autoAcceptedSuggestions = getNodes(
    autoAcceptedResult.data?.aiSuggestions
  ) as ReviewAiSuggestionsFromNotification_AiSuggestionFragment[];

  return {
    ...suggestionsQueryResult,
    // Fold in the auto-accepted query so cards do not briefly show their empty
    // states before the saved counts load.
    loading:
      suggestionsQueryResult.loading ||
      householdQueryResult.loading ||
      autoAcceptedResult.loading,
    error,
    suggestions,
    autoAcceptedSuggestions,
    householdName: household?.displayName ?? null,
  };
}
